import React, { useEffect } from 'react';
import { modDatabase } from 'data/mods';
import { UNIQUE_IDOLS } from './constants/uniqueIdols';
import TwistedWishForm from './TwistedWishForm';
import ValueModifierInput from './ValueModifierInput';
import { 
  UniqueIdolFormProps, 
  FixedModifier, 
  FixedValueModifier, 
  VariableModifier 
} from './types';

const UniqueIdolForm: React.FC<UniqueIdolFormProps> = ({
  selectedUniqueIdol,
  setSelectedUniqueIdol,
  idolModifiers,
  selectedVariableModifiers,
  setNewIdolName,
  updateModifierValue,
  replaceVariableModifier,
  isVariableModifierSelected,
  isEditing = false
}) => {
  // Update idol name when selection changes
  useEffect(() => {
    if (selectedUniqueIdol) {
      const idol = UNIQUE_IDOLS.find(i => i.id === selectedUniqueIdol);
      if (idol) {
        setNewIdolName(idol.name);
      }
    }
  }, [selectedUniqueIdol, setNewIdolName]);

  // Get the selected idol data
  const selectedIdolData = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);

  // Filter to get different types of modifiers
  const fixedNonAdjustableModifiers = idolModifiers.filter(
    mod => mod.isFixed && !mod.isAdjustable
  ) as FixedModifier[];
  
  const fixedAdjustableModifiers = idolModifiers.filter(
    mod => mod.isFixed && mod.isAdjustable
  ) as FixedValueModifier[];
  
  const variableModifiers = idolModifiers.filter(
    mod => !mod.isFixed
  ) as VariableModifier[];

  return (
    <div className="mb-4">
      <div className="mb-4">
        <label className="block text-sm mb-1">Unique Idol Type</label>
        <select
          value={selectedUniqueIdol}
          onChange={(e) => setSelectedUniqueIdol(e.target.value)}
          className={`w-full p-2 rounded ${
            isEditing ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-700 text-white'
          }`}
          disabled={isEditing} // Disable changing the unique idol type when editing
        >
          <option value="">Select Unique Idol</option>
          {UNIQUE_IDOLS.map(idol => (
            <option key={idol.id} value={idol.id}>
              {idol.name}
            </option>
          ))}
        </select>
        {isEditing && (
          <div className="text-xs text-gray-400 mt-1">
            Unique idol type cannot be changed when editing
          </div>
        )}
      </div>

      {selectedIdolData && (
        <div className="mt-4">
          <h3 className="text-md font-semibold mb-2">{selectedIdolData.name} Modifiers</h3>
          
          {/* Handle special case for Twisted Wish or similar complex idols */}
          {selectedIdolData.isSpecial ? (
            <TwistedWishForm 
              idolModifiers={idolModifiers}
              selectedVariableModifiers={selectedVariableModifiers}
              updateModifierValue={updateModifierValue}
              replaceVariableModifier={replaceVariableModifier}
              isVariableModifierSelected={isVariableModifierSelected}
              isEditing={isEditing}
            />
          ) : (
            <>
              {/* Display fixed non-adjustable modifiers - read only */}
              {fixedNonAdjustableModifiers.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm text-white mb-2">Fixed Modifiers:</h4>
                  <div className="space-y-2">
                    {fixedNonAdjustableModifiers.map((mod) => (
                      <div key={mod.modId} className="bg-gray-700 p-2 rounded">
                        <span className="text-white">
                          {modDatabase.mods[mod.modId]?.description || `Modifier #${mod.modId}`}
                          {mod.defaultValue ? `: ${mod.defaultValue}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display fixed adjustable modifiers with value inputs */}
              {fixedAdjustableModifiers.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm text-white mb-2">Fixed Modifiers with Adjustable Values:</h4>
                  <div className="space-y-3">
                    {fixedAdjustableModifiers.map((mod) => (
                      <ValueModifierInput
                        key={mod.modId}
                        modifier={mod}
                        onChange={(value) => updateModifierValue(mod.modId, value)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display variable modifiers (if any were selected) */}
              {variableModifiers.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm text-white mb-2">Variable Modifiers:</h4>
                  <div className="space-y-3">
                    {variableModifiers.map((mod) => (
                      <ValueModifierInput
                        key={mod.modId}
                        modifier={mod}
                        onChange={(value) => updateModifierValue(mod.modId, value)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UniqueIdolForm;